import { useEffect, useState } from 'react'
import { Link, NavLink } from 'react-router-dom'

export type HeaderTone = 'light' | 'dark'

const NAV = [
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
  { to: '/registry', label: 'Registry' },
  { to: '/store', label: 'Store' },
]

/**
 * Transparent header laid over the first section of every page: wordmark left, nav + "Register Now" right, and a
 * full-screen overlay menu on small screens.
 */
export default function Header({ tone = 'light' }: { tone?: HeaderTone }) {
  const [open, setOpen] = useState(false)
  const close = () => setOpen(false)

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const text = tone === 'light' ? 'text-white' : 'text-black'
  const cta = tone === 'light' ? 'btn-white' : 'btn-gray'
  const navLink = ({ isActive }: { isActive: boolean }) =>
    `no-underline hover:underline underline-offset-8 decoration-1 ${isActive ? 'underline' : ''}`

  return (
    <header className={`absolute inset-x-0 top-0 z-30 ${text}`}>
      <div className="gutter flex items-center justify-between py-6 md:py-9">
        <Link
          to="/"
          className="font-display text-[2rem] leading-none no-underline md:text-[2.4rem]"
          aria-label="ColorMark home"
        >
          ColorMark
        </Link>

        <nav className="hidden items-center gap-7 text-[1.1rem] md:flex" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} className={navLink}>
              {item.label}
            </NavLink>
          ))}
          <Link to="/register" className={`btn ${cta} ml-3`}>
            Register Now
          </Link>
        </nav>

        <button
          type="button"
          className="relative z-50 flex h-10 w-10 flex-col items-center justify-center gap-[7px] md:hidden"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((v) => !v)}
        >
          <span
            className={`block h-[2px] w-7 bg-current transition-transform ${open ? 'translate-y-[4.5px] rotate-45 bg-white!' : ''}`}
          />
          <span
            className={`block h-[2px] w-7 bg-current transition-transform ${open ? '-translate-y-[4.5px] -rotate-45 bg-white!' : ''}`}
          />
        </button>
      </div>

      {open && (
        <div
          id="mobile-menu"
          className="fixed inset-0 z-40 flex flex-col justify-between bg-black px-8 pb-10 pt-28 text-white md:hidden"
        >
          <nav className="flex flex-col gap-6" aria-label="Mobile">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} onClick={close} className="font-display text-4xl no-underline">
                {item.label}
              </NavLink>
            ))}
          </nav>
          <Link to="/register" onClick={close} className="btn btn-white w-full">
            Register Now
          </Link>
        </div>
      )}
    </header>
  )
}
